import React, { useState } from 'react';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { useAppAuth } from '../context/AuthContext';
import './ProtectedRoute.css';

export default function ProtectedRoute({ children, minimumRole = 'viewer', showShell = false }) {
  const { isLoaded, isSignedIn, user, hasRole } = useAppAuth();
  const { signIn } = useSignIn();
  const { signOut } = useClerk();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState('');

  const handleMicrosoftSignIn = async () => {
    setError('');
    setSigningIn(true);
    try {
      const origin = window.location.origin;
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_microsoft',
        redirectUrl: `${origin}/sso-callback`,
        redirectUrlComplete: `${origin}/panel`,
      });
    } catch (err) {
      setError('Sign in failed. Please try again.');
      setSigningIn(false);
    }
  };

  if (!isLoaded) {
    return showShell ? children : null;
  }

  if (!isSignedIn) {
    return (
      <section className="protected-route-page">
        <div className="protected-route-container">
          <div className="protected-route-card">
            <img src="/logo.png" alt="TheTopia" className="protected-route-logo" />
            <h1 className="protected-route-title">Admin Sign In</h1>
            <p className="protected-route-subtitle">For TheTopia team members</p>

            {error && <div className="protected-route-error">{error}</div>}

            <button
              className="microsoft-sign-in-button"
              onClick={handleMicrosoftSignIn}
              disabled={signingIn}
            >
              <svg className="microsoft-logo" viewBox="0 0 21 21" width="20" height="20">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {signingIn ? 'Redirecting...' : 'Sign in with Microsoft'}
            </button>

            <p className="protected-route-legal">
              By continuing, you agree to TheTopia's terms of service.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!hasRole(minimumRole)) {
    const email = user?.primaryEmailAddress?.emailAddress;
    return (
      <section className="protected-route-page">
        <div className="protected-route-container">
          <div className="protected-route-card protected-route-denied">
            <div className="protected-route-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cc3333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h1 className="protected-route-title">Access Denied</h1>
            {email && (
              <p className="protected-route-account">
                Signed in as <strong>{email}</strong>
              </p>
            )}
            <p className="protected-route-subtitle">
              This account doesn't have permission to view this page. Try signing in with a different account, or contact an administrator.
            </p>
            <button
              className="microsoft-sign-in-button"
              onClick={() => signOut({ redirectUrl: '/panel' })}
            >
              <svg className="microsoft-logo" viewBox="0 0 21 21" width="20" height="20">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Use a Different Account
            </button>
          </div>
        </div>
      </section>
    );
  }

  return children;
}
